import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Grid,
  Row,
  Select,
  Space,
  Spin,
  Tooltip,
  Typography,
} from 'antd';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import FourZeroThree from '../../FourZeroThree';
import FiveHundred from '../../FiveHundred';
import SellersDropdown from '../../sellers/SellersDropdown';
import { CloseCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  DATE_FORMAT,
  DEFAULT_FONT_SIZE,
  DEFAULT_SIZE_TYPE,
  GENERAL_TIMEOUT,
} from '../../../constants/systemConstants';
import moment from 'moment';
import {
  FontSizeType,
  GoodData,
  OrderClaimGoodsStatData,
  OrderEnum,
} from '../../../types';
import { SizeType } from 'antd/lib/config-provider/SizeContext';
import { useLocalStorage } from '../../../hooks/useLocalStorage';
import { useVT } from 'virtualizedtableforantd4';
import { getDataWithAuthToken } from '../../../utils/axiosRequest';
import { alertMessage } from '../../../utils/alertMessage';
import SelectGoodsModal from '../../goods/selectGoods/SelectGoodsModal';
import TableToolbar from '../../table/TableToolbar';
import Table, { ColumnsType, TablePaginationConfig } from 'antd/lib/table';
import { FilterValue, SorterResult } from 'antd/lib/table/interface';
import { hasPermission } from '../../../utils/hasPermission';
import { actionPermissions } from '../../../constants/actionPermissions';
import { useTab } from '../../../hooks/useTab';
import { setFont } from '../../../utils/colComponents';
import { dashboardRoute } from '../../../constants/pathname';
import TableFooterToolbar from '../../table/TableFooterToolbar';

type OrderClaimGoodsDetailProps = {
  isSeller: boolean;
  isLoading: boolean;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
};

const OrderClaimGoodsDetail = ({
  isSeller,
  isLoading,
  setIsLoading,
}: OrderClaimGoodsDetailProps) => {
  //General Components
  const [fourZeroThree, setFourZeroThree] = useState<boolean>(false);
  const [fiveHundred, setFiveHundred] = useState(false);
  const { addTab } = useTab();
  const { RangePicker } = DatePicker;
  const isSubscribed = useRef(true);
  const formRef = useRef(null);
  const [form] = Form.useForm();
  const { t } = useTranslation();
  //Text Components
  const [typingTimeout, setTypingTimeout] = useState<NodeJS.Timeout>();
  //Data Components
  const [selectGoodsVisible, setSelectGoodsVisible] = useState<boolean>(false);
  const [selectedGoodObjs, setSelectedGoodObjs] = useState<GoodData>();
  const [good, setGood] = useState<GoodData>();
  const [claimGoodsStatData, setClaimGoodsStatData] =
    useState<OrderClaimGoodsStatData[]>();
  const [enums, setEnums] = useState<OrderEnum>();
  const [sortValue, setSortValue] = useState<
    'CLAIM_NUM' | 'CLAIM_TOTAL_AMOUNT'
  >('CLAIM_NUM');
  // Table Components
  const [fontSize, setFontSize] = useState<FontSizeType>(DEFAULT_FONT_SIZE);
  const [tableSize, setTableSize] = useState<SizeType>(DEFAULT_SIZE_TYPE);
  const screens = Grid.useBreakpoint();
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useLocalStorage('pageSize', '10');
  const [totalItem, setTotalItem] = useState(0);
  const [vt] = useVT(() => ({ scroll: { y: 600 } }), []);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<OrderClaimGoodsStatData[]>(
    []
  );

  const columnKeys = [
    'goodsId',
    'fullGoodsName',
    'claimIdNumber',
    'claimNumber',
    'totalAmount',
    'claimAmount|depositFee|recycleFee|gstFee|pstFee',
  ];
  const [selectedColumns, setSelectedColumns] = useState(
    columnKeys.filter((key) => key !== 'goodsId')
  );

  const columns: ColumnsType<OrderClaimGoodsStatData> = [
    {
      title: setFont(t('goods.goodsListColumns.goodsId'), fontSize),
      key: 'goodsId',
      dataIndex: 'goodsId',
      fixed: screens.lg ? 'left' : undefined,
      width: 20,
      render: (value) => (
        <Button
          style={{ padding: 0, fontSize: fontSize }}
          type="link"
          onClick={() => {
            addTab('', `${dashboardRoute.goods.detail}?good_id=${value}`);
          }}
          disabled={!hasPermission(actionPermissions.goodGroup.goodView)}
        >
          {value}
        </Button>
      ),
    },
    {
      title: setFont(t('goods.goodsListColumns.goodsName'), fontSize),
      key: 'fullGoodsName',
      dataIndex: 'fullGoodsName',
      width: 80,
      render: (value, record) =>
        record &&
        hasPermission(actionPermissions.goodGroup.goodView) && (
          <Tooltip title={value}>
            <Button
              type="link"
              style={{
                padding: 0,
                textAlign: 'left',
                whiteSpace: 'normal',
                height: 'auto',
              }}
              disabled={!hasPermission(actionPermissions.goodGroup.goodView)}
              onClick={() => {
                addTab(
                  '',
                  `${dashboardRoute.goods.detail}?good_id=${record.goodsId}`
                );
              }}
            >
              {value}
            </Button>
          </Tooltip>
        ),
    },
    {
      title: setFont(t('analysis.orderClaimGoods.claimIdNumber'), fontSize),
      key: 'claimIdNumber',
      dataIndex: 'claimIdNumber',
      width: 40,
      render: (value, record) => (
        <Button
          style={{ padding: 0, fontSize: fontSize }}
          type="link"
          onClick={() => {
            addTab(
              '',
              `${dashboardRoute.order.orderClaimList}?claim_ids=${record.claimIds}`
            );
          }}
          disabled={!hasPermission(actionPermissions.orderGroup.orderClaimView)}
        >
          {value}
        </Button>
      ),
    },
    {
      title: setFont(t('analysis.orderClaimGoods.claimNumber'), fontSize),
      key: 'claimNumber',
      dataIndex: 'claimNumber',
      width: 40,
      sorter: true,
    },
    {
      title: setFont(t('analysis.orderClaimGoods.totalAmount'), fontSize),
      key: 'totalAmount',
      dataIndex: 'totalAmount',
      width: 50,
      sorter: true,
    },
    {
      title: setFont(
        `${t('analysis.orderClaimGoods.claimAmount')}`,
        fontSize,
        `${t('analysis.orderClaimGoods.depositFee')}|${t(
          'analysis.orderClaimGoods.recycleFee'
        )}|${t('analysis.orderClaimGoods.gstFee')}|${t(
          'analysis.orderClaimGoods.pstFee'
        )}`
      ),
      key: 'claimAmount|depositFee|recycleFee|gstFee|pstFee',
      width: 80,
      sorter: true,
      render: (record: OrderClaimGoodsStatData) => (
        <Space direction="vertical" size={0}>
          <Typography.Text key={'claimAmount'} style={{ fontSize: fontSize }}>
            {`${t(`analysis.orderClaimGoods.claimAmount`)}: ${record.claimAmount}`}
          </Typography.Text>
          <Typography.Text key={'depositFee'} style={{ fontSize: fontSize }}>
            {`${t(`analysis.orderClaimGoods.depositFee`)}: ${record.depositFee}`}
          </Typography.Text>
          <Typography.Text key={'recycleFee'} style={{ fontSize: fontSize }}>
            {`${t(`analysis.orderClaimGoods.recycleFee`)}: ${record.recycleFee}`}
          </Typography.Text>
          <Typography.Text key={'gstFee'} style={{ fontSize: fontSize }}>
            {`${t(`analysis.orderClaimGoods.gstFee`)}: ${record.gstFee}`}
          </Typography.Text>
          <Typography.Text key={'pstFee'} style={{ fontSize: fontSize }}>
            {`${t(`analysis.orderClaimGoods.pstFee`)}: ${record.pstFee}`}
          </Typography.Text>
        </Space>
      ),
    },
  ];

  const getEnums = async () => {
    try {
      const response = await getDataWithAuthToken(`order/enum_list`);
      if (response && response.goodStatus) {
        if (isSubscribed.current) {
          setEnums(response.data);
        } else {
          alertMessage(
            'error',
            response?.msg || t('general.noResponse'),
            response?.data || undefined
          );
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getFormParams = useCallback(() => {
    return {
      page: page,
      size: pageSize,
      sortValue: sortValue,
      isSeller: isSeller,
      sellerId:
        formRef.current && form.getFieldValue('sellerId')
          ? form.getFieldValue('sellerId')
          : undefined,

      startDate:
        formRef.current && form.getFieldValue('date')
          ? moment(form.getFieldValue('date')[0]).format(DATE_FORMAT)
          : undefined,
      endDate:
        formRef.current && form.getFieldValue('date')
          ? moment(form.getFieldValue('date')[1]).format(DATE_FORMAT)
          : undefined,

      startExpectShippingTime:
        formRef.current && form.getFieldValue('shippingDate')
          ? moment(form.getFieldValue('shippingDate')[0]).format(DATE_FORMAT)
          : undefined,
      endExpectShippingTime:
        formRef.current && form.getFieldValue('shippingDate')
          ? moment(form.getFieldValue('shippingDate')[1]).format(DATE_FORMAT)
          : undefined,

      goodsId:
        formRef.current && form.getFieldValue('goodsId')
          ? form.getFieldValue('goodsId')
          : undefined,

      claimType:
        formRef.current && form.getFieldValue('claimType')
          ? form.getFieldValue('claimType').join()
          : undefined,

      reasonType:
        formRef.current && form.getFieldValue('reasonType')
          ? form.getFieldValue('reasonType')
          : undefined,
    };
  }, [page, pageSize, sortValue, isSeller, form]);

  const getData = useCallback(async () => {
    try {
      if (isSubscribed.current) setIsLoading(true);
      const response = await getDataWithAuthToken(
        'analysis/order/claim_goods',
        {
          params: getFormParams(),
        }
      );

      if (response && response.goodStatus) {
        setClaimGoodsStatData(response.data.list);
        setTotalItem(response.data.totalItem);
      } else if (response && response.returnCode === 403) {
        setFourZeroThree(true);
      } else {
        setFiveHundred(true);
        alertMessage(
          'error',
          response?.msg || t('general.noResponse'),
          response?.data || undefined
        );
      }
      setIsLoading(false);
    } catch (error) {
      console.log(error);
      setIsLoading(false);
    }
  }, [getFormParams, t, setIsLoading]);

  const resetData = () => {
    form.resetFields();
    setPage(1);
    setClaimGoodsStatData([]);
    setSelectedRows([]);
    setSelectedGoodObjs(undefined);
    setGood(undefined);
    getData();
  };

  useEffect(() => {
    getData();
  }, [getData]);

  useEffect(() => {
    if (selectedGoodObjs) {
      form.setFieldsValue({ goodsId: selectedGoodObjs.goodsId });
      setGood(selectedGoodObjs);
      setSelectedGoodObjs(undefined);
    }
  }, [selectedGoodObjs, form]);

  const onTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter:
      | SorterResult<OrderClaimGoodsStatData>
      | SorterResult<OrderClaimGoodsStatData>[],
    extra: {
      currentDataSource: OrderClaimGoodsStatData[];
      action: 'paginate' | 'sort' | 'filter';
    }
  ) => {
    if (extra.action === 'sort' && !Array.isArray(sorter)) {
      if (sorter.field === 'claimNumber') {
        setSortValue('CLAIM_NUM');
      } else if (sorter.field === 'totalAmount') {
        setSortValue('CLAIM_TOTAL_AMOUNT');
      }
    }
  };

  return (
    <>
      {fourZeroThree ? (
        <Card>
          <FourZeroThree />
        </Card>
      ) : fiveHundred ? (
        <Card>
          <FiveHundred />
        </Card>
      ) : (
        <Spin style={{ width: '100%' }} spinning={isLoading}>
          <Form style={{ paddingBottom: 20 }} form={form} ref={formRef}>
            <Row gutter={[16, 0]}>
              {isSeller && (
                <Col span={24} md={24} lg={24} xl={6}>
                  <Form.Item
                    name="sellerId"
                    label={t('analysis.goodsSales.seller')}
                  >
                    <SellersDropdown />
                  </Form.Item>
                </Col>
              )}
              <Col span={24} md={24} lg={24} xl={9}>
                <Form.Item
                  name={'shippingDate'}
                  label={t('analysis.orderClaimGoods.shippingDate')}
                  initialValue={[moment(), moment()]}
                >
                  <RangePicker
                    format={DATE_FORMAT}
                    style={{ width: '100%' }}
                    placeholder={[t('nivo.startDate'), t('nivo.endDate')]}
                    ranges={{
                      [`${t('nivo.week')}`]: [
                        moment().startOf('isoWeek'),
                        moment().endOf('isoWeek'),
                      ],
                      [`${t('nivo.month')}`]: [
                        moment().startOf('month'),
                        moment().endOf('month'),
                      ],
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={24} md={24} lg={24} xl={9}>
                <Form.Item
                  name={'date'}
                  label={t('analysis.orderClaimGoods.date')}
                >
                  <RangePicker
                    format={DATE_FORMAT}
                    style={{ width: '100%' }}
                    placeholder={[t('nivo.startDate'), t('nivo.endDate')]}
                    ranges={{
                      [`${t('nivo.week')}`]: [
                        moment().startOf('isoWeek'),
                        moment().endOf('isoWeek'),
                      ],
                      [`${t('nivo.month')}`]: [
                        moment().startOf('month'),
                        moment().endOf('month'),
                      ],
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={24} md={24} lg={24} xl={8}>
                <Form.Item
                  name="claimType"
                  label={t('order.claimListColumns.advancedSearch.claimType')}
                >
                  <Select
                    mode="multiple"
                    getPopupContainer={(triggerNode) => triggerNode.parentNode}
                    onFocus={() => {
                      !enums && getEnums();
                    }}
                    allowClear
                  >
                    {enums && enums.claimType
                      ? enums.claimType.map((type) => (
                          <Select.Option
                            key={type.description}
                            value={type.code}
                          >
                            {type.description}
                          </Select.Option>
                        ))
                      : undefined}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={24} md={24} lg={24} xl={8}>
                <Form.Item
                  name="reasonType"
                  label={t('order.orderDetail.reasonType')}
                >
                  <Select
                    getPopupContainer={(triggerNode) => triggerNode.parentNode}
                    onFocus={() => {
                      !enums && getEnums();
                    }}
                    allowClear
                  >
                    {enums && enums.claimReasonType
                      ? enums.claimReasonType.map((type) => (
                          <Select.Option
                            key={type.description}
                            value={type.code}
                          >
                            {type.description}
                          </Select.Option>
                        ))
                      : undefined}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={24} md={12} lg={12} xl={4}>
                <Form.Item
                  name="goodsId"
                  label={t('order.orderListColumns.advancedSearch.goods')}
                  style={{ marginBottom: 12 }}
                >
                  <Space>
                    {good ? (
                      <Space>
                        <Typography.Text
                          ellipsis={{ tooltip: good.fullGoodsName }}
                          style={{ maxWidth: 100 }}
                        >
                          {good.fullGoodsName}
                        </Typography.Text>
                        <CloseCircleOutlined
                          onClick={() => {
                            form.resetFields(['goodsId']);
                            setGood(undefined);
                          }}
                        />
                      </Space>
                    ) : (
                      <></>
                    )}
                    <Button
                      type="link"
                      onClick={() => setSelectGoodsVisible(true)}
                      style={{ padding: 0 }}
                    >
                      {t('actionsColumn.selectGood')}
                    </Button>
                  </Space>
                </Form.Item>
              </Col>
              <Col span={24}>
                <Space align="start">
                  <Button
                    htmlType="submit"
                    type="primary"
                    onClick={() => {
                      if (typingTimeout) clearTimeout(typingTimeout);
                      setTypingTimeout(
                        setTimeout(() => {
                          getData();
                        }, GENERAL_TIMEOUT)
                      );
                    }}
                  >
                    {t('general.ok')}
                  </Button>
                  <Button
                    onClick={() => {
                      if (typingTimeout) clearTimeout(typingTimeout);
                      setTypingTimeout(
                        setTimeout(() => {
                          resetData();
                        }, GENERAL_TIMEOUT)
                      );
                    }}
                  >
                    {t('general.reset')}
                  </Button>
                </Space>
                <Tooltip title={t('general.refresh')}>
                  <Button
                    type="text"
                    onClick={() => {
                      if (typingTimeout) clearTimeout(typingTimeout);
                      setTypingTimeout(
                        setTimeout(() => {
                          getData();
                        }, GENERAL_TIMEOUT)
                      );
                    }}
                    icon={<ReloadOutlined />}
                  />
                </Tooltip>
              </Col>
            </Row>
          </Form>

          <TableToolbar
            setFontSize={setFontSize}
            fontSize={fontSize}
            tableSize={tableSize}
            setTableSize={setTableSize}
            refresh={() => getData()}
            totalItems={totalItem}
            columns={columns}
            columnKeys={columnKeys}
            selectedColumns={selectedColumns}
            setSelectedColumns={setSelectedColumns}
            rows={claimGoodsStatData}
            exportConfig={{
              fileName: 'CLAIM_GOODS_STATS',
            }}
          />

          <Table<OrderClaimGoodsStatData>
            onChange={onTableChange}
            dataSource={claimGoodsStatData}
            loading={isLoading}
            size={tableSize}
            columns={columns.filter((x) =>
              selectedColumns.includes(x.key?.toString() ?? '')
            )}
            components={vt}
            scroll={{ y: 600, x: 1200 }}
            rowKey={(row) => `${row.goodsId}`}
            rowSelection={{
              selectedRowKeys,
              onChange: (
                selectedRowKeys: React.Key[],
                selectedRows: OrderClaimGoodsStatData[]
              ) => {
                setSelectedRowKeys(selectedRowKeys);
                setSelectedRows(selectedRows);
              },
            }}
            pagination={{
              current: page,
              total: totalItem,
              showQuickJumper: true,
              showSizeChanger: true,
              showTotal: (total, range) =>
                t('general.paginationTotal', {
                  start: range[0],
                  end: range[1],
                  total: total,
                }),
              size: 'small',
              defaultPageSize: pageSize,
              onChange: (p, pSize) => {
                setPage(p);
                setPageSize(pSize || pageSize);
                setSelectedRowKeys([]);
              },
            }}
          />

          <SelectGoodsModal
            firstLoad={false}
            newGoodObjs={selectedGoodObjs}
            setNewGoodObjs={setSelectedGoodObjs}
            visible={selectGoodsVisible}
            setVisible={setSelectGoodsVisible}
            sellerId={
              form.getFieldValue('sellerId')
                ? Number(form.getFieldValue('sellerId'))
                : 0
            }
            multiSelectFeature={false}
          />

          {!!selectedRowKeys.length && (
            <TableFooterToolbar
              selectedRows={selectedRows}
              setSelectedRowKeys={setSelectedRowKeys}
              columns={columns}
              funct={{ exportConfig: { fileName: 'CLAIM_GOODS_STATS' } }}
            />
          )}
        </Spin>
      )}
    </>
  );
};

export default OrderClaimGoodsDetail;
